import {
  AvatarDropdown,
  AvatarName,
  Footer,
  Question,
  SelectLang,
  ThemeDropdown,
  AlertBell,
} from '@/components';
import {
  currentUser as queryCurrentUser,
  optionDictDataSelect,
  fetchMenuData,
  getSystemConfig,
} from '@/services/ant-design-pro/api';
import { LinkOutlined } from '@ant-design/icons';
import { SettingDrawer, MenuDataItem, Settings as LayoutSettings } from '@ant-design/pro-layout';
import { createFromIconfontCN } from '@ant-design/icons';
import type { RunTimeLayoutConfig } from '@umijs/max';
import { history, Link } from '@umijs/max';
import { ConfigProvider, App } from 'antd';
import zhCN from 'antd/locale/zh_CN';

// 导入 dayjs 配置
import '@/utils/dayjs';
import { injectGlobalStyles } from '@/global';

import React from 'react';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';

const systemConfig = { ...defaultSettings };

// 从localStorage获取保存的主题设置
const getSavedSettings = () => {
  try {
    const savedSettings = localStorage.getItem('theme-settings');
    const settings = savedSettings ? JSON.parse(savedSettings) : defaultSettings;
    return settings;
  } catch {
    return defaultSettings;
  }
};

// 应用主题设置到文档 - 优化版本
const applyThemeSettings = (settings: Partial<LayoutSettings>) => {
  const { navTheme } = settings;
  // Access primaryColor safely using type assertion
  const primaryColor = (settings as any).token?.colorPrimary || (settings as any).primaryColor;

  // 获取当前主题设置，支持 system 主题
  const savedTheme = localStorage.getItem('app-theme') || 'system';
  let shouldUseDark = false;

  if (savedTheme === 'realDark') {
    shouldUseDark = true;
  } else if (savedTheme === 'light') {
    shouldUseDark = false;
  } else if (savedTheme === 'system') {
    // 检查系统主题偏好
    shouldUseDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  } else if (navTheme === 'realDark') {
    shouldUseDark = true;
  }

  // 立即应用暗黑模式，避免闪烁
  if (shouldUseDark) {
    document.documentElement.setAttribute('data-theme', 'realDark');
    document.body.classList.add('dark');
    document.body.classList.remove('light');
    // 立即设置背景色
    document.documentElement.style.setProperty('--bg-color', '#141414');
    document.documentElement.style.setProperty('--text-color', '#ffffff');
  } else {
    document.documentElement.removeAttribute('data-theme');
    document.body.classList.add('light');
    document.body.classList.remove('dark');
    // 立即设置背景色
    document.documentElement.style.setProperty('--bg-color', '#ffffff');
    document.documentElement.style.setProperty('--text-color', '#000000');
  }

  // 如果有主色调，则应用主色调
  if (primaryColor) {
    document.documentElement.style.setProperty('--ant-primary-color', primaryColor);
  }
};

const IconFont = createFromIconfontCN({
  scriptUrl: [
    '//at.alicdn.com/t/font_2713835_daepmvl8rp4.js',
    // '//at.alicdn.com/t/font_3418336_n2fh4bof259.js',
    '//at.alicdn.com/t/c/font_2713835_x7ngtq8folo.js',
  ],
});

const JumpLogin = () => {
  const { search, pathname } = history.location;
  if (!pathname.includes(loginPath)) {
    const params = new URLSearchParams({
      redirect: pathname + search,
    });
    history.replace({
      pathname: loginPath,
      search: params.toString(),
    });
  }
};

/**
 * @see  https://umijs.org/zh-CN/plugins/plugin-initial-state
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  appConfig?: Record<string, any>;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
  sexSelectOption?: API.DictDataListItem[];
  fetchSexSelect?: () => Promise<API.DictDataListItem[] | undefined>;
  showHideSelectOption?: API.DictDataListItem[];
  fetchShowHideSelect?: () => Promise<API.DictDataListItem[] | undefined>;
  normalDisableSelectOption?: API.DictDataListItem[];
  fetchNormalDisableSelect?: () => Promise<API.DictDataListItem[] | undefined>;
  menuTypeSelectOption?: API.DictDataListItem[];
  scopeTypeSelectOption?: API.DictDataListItem[];
  fetchSystemSConfig?: () => Promise<Record<string, any> | undefined>;
}> {
  const fetchSexSelect = async () => {
    try {
      const sexSelectOption = await optionDictDataSelect({ dictType: 'sys_user_sex' });
      return sexSelectOption.data;
    } catch {
      // Error handling for sex select option
    }
    return undefined;
  };
  const fetchUserInfo = async () => {
    try {
      const msg = await queryCurrentUser({
        skipErrorHandler: true,
      });
      return msg.data;
    } catch {
      // history.push(loginPath);
      JumpLogin();
    }
    return undefined;
  };
  const fetchSystemSConfig = async () => {
    try {
      const msg = await getSystemConfig();
      return msg.data;
    } catch {
      // Error handling for system config
    }
    return undefined;
  };
  const appConfig = await fetchSystemSConfig();
  // 获取保存的主题设置并立即应用
  const savedSettings = getSavedSettings();

  // 注入全局样式（CSS-in-JS）
  if (typeof window !== 'undefined') {
    injectGlobalStyles();
  }

  // 在任何环境中都立即应用主题，避免白屏
  applyThemeSettings(savedSettings);

  // 如果不是登录页面，执行
  const { location } = history;
  if (location.pathname !== loginPath && localStorage.getItem('token')) {
    const currentUser = await fetchUserInfo();
    const sexSelectOption = await fetchSexSelect();
    const showHideSelectOption = [
      { label: '显示', value: false },
      { label: '隐藏', value: true },
    ];
    const menuTypeSelectOption = [
      { label: '目录', value: 'M' },
      { label: '菜单', value: 'C' },
      { label: '按钮', value: 'F' },
    ];
    const scopeTypeSelectOption = [
      { value: '1', label: '全部数据权限' },
      { value: '2', label: '自定数据权限' },
      { value: '3', label: '本部门数据权限' },
      { value: '4', label: '本部门及以下数据权限' },
      { value: '5', label: '仅本人数据权限' },
    ];
    const normalDisableSelectOption = [
      { value: '2', label: '正常' },
      { value: '1', label: '停用' },
    ];
    return {
      fetchUserInfo,
      fetchSystemSConfig,
      currentUser,
      appConfig,
      fetchSexSelect,
      sexSelectOption,
      normalDisableSelectOption,
      showHideSelectOption,
      menuTypeSelectOption,
      scopeTypeSelectOption,
      settings: savedSettings, // 使用保存的设置
    };
  }
  return {
    appConfig,
    fetchSystemSConfig,
    fetchUserInfo,
    fetchSexSelect,
    settings: savedSettings, // 使用保存的设置
  };
}

const fixMenuItemIcon = (menus: MenuDataItem[]): MenuDataItem[] => {
  menus.forEach((item) => {
    const { icon, children } = item;
    if (typeof icon === 'string') {
      item.icon = React.createElement(() => {
        return <IconFont type={icon} />;
      });
    }
    if (children && children.length > 0) {
      item.children = fixMenuItemIcon(children);
    }
  });
  return menus;
};

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({ initialState, setInitialState }) => {
  return {
    actionsRender: () => [
      <AlertBell key="alertBell" />,
      <Question key="doc" />,
      <SelectLang key="SelectLang" />,
      <ThemeDropdown
        key="theme"
        onThemeChange={(theme) => {
          // Ensure theme is compatible with InitialState type (only "realDark" or "light")
          const navTheme = theme === 'system' ? 'light' : (theme as 'realDark' | 'light');
          const newSettings = {
            ...initialState?.settings,
            navTheme,
            logo: initialState?.appConfig?.sys_site_name || systemConfig.logo,
            title: initialState?.appConfig?.sys_app_name || systemConfig.title,
          };
          // 保存设置到localStorage
          localStorage.setItem('theme-settings', JSON.stringify(newSettings));
          // 立即应用主题设置
          applyThemeSettings(newSettings);
          setInitialState((preInitialState) => {
            if (!preInitialState) return { settings: newSettings };
            return {
              ...preInitialState,
              settings: newSettings,
            };
          });
        }}
      />,
    ],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown menu={true}>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      content: initialState?.currentUser?.name,
    },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { search, pathname } = history.location;

      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && !pathname.includes(loginPath)) {
        const params = new URLSearchParams({
          redirect: pathname + search,
        });
        history.replace({
          pathname: loginPath,
          search: params.toString(),
        });
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: isDev
      ? [
          <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
            <LinkOutlined />
            <span>OpenAPI 文档</span>
          </Link>,
        ]
      : [],
    menuHeaderRender: undefined,
    childrenRender: (children) => {
      return (
        <>
          {children}
          <SettingDrawer
            disableUrlParams
            enableDarkTheme
            settings={initialState?.settings}
            onSettingChange={(settings) => {
              // 保存设置到localStorage
              localStorage.setItem('theme-settings', JSON.stringify(settings));
              // 立即应用主题设置
              applyThemeSettings(settings);
              setInitialState((preInitialState) => ({
                ...preInitialState,
                settings: {
                  ...settings,
                  title: initialState?.appConfig?.sys_app_name || defaultSettings.title,
                  logo: initialState?.appConfig?.sys_site_name || defaultSettings.logo,
                },
              }));
            }}
          />
        </>
      );
    },
    ...initialState?.settings,
    logo: initialState?.appConfig?.sys_site_name || defaultSettings.logo,
    title: initialState?.appConfig?.sys_app_name || defaultSettings.title,
    menu: {
      params: {
        userId: initialState?.currentUser?.userid,
      },
      request: async (): Promise<MenuDataItem[]> => {
        if (initialState?.currentUser?.userid) {
          const menuData = await fetchMenuData();
          return (menuData.data || []) as MenuDataItem[];
        }
        return []; // Return empty array when userid is not available
      },
    },
    menuDataRender: (menuData) => {
      return fixMenuItemIcon(menuData);
    },
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request = {
  ...errorConfig,
};

// 配置 Antd 的 ConfigProvider 和 App 组件
export function rootContainer(container: React.ReactNode) {
  return (
    <ConfigProvider locale={zhCN}>
      <App>{container}</App>
    </ConfigProvider>
  );
}
